import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, LayoutDashboard, LifeBuoy } from 'lucide-react';
import { Logo } from '../components/Logo.jsx';
import { useAuth } from '../lib/auth.jsx';

const CONTACT_EMAIL = 'distrx.io@gmail.com';

/**
 * The catch-all for a URL that matches no route.
 *
 * Two things a 404 has to do and the old one did neither: say where you are,
 * and give you a way out that is not the browser's back button alone. It also
 * has to answer "out to where?" differently depending on who is looking — a
 * signed-in merchant wants their dashboard, a visitor wants the front page, and
 * sending a signed-out visitor to /dashboard just bounces them to a sign-in
 * screen they did not ask for.
 */
export function NotFoundPage() {
  const { isAuthenticated, isPlatformAdmin, restoring } = useAuth();
  const { pathname } = useLocation();

  const home = isPlatformAdmin ? '/admin' : '/dashboard';

  return (
    <div className="flex min-h-screen flex-col" style={{ background: 'rgb(var(--surface))' }}>
      <header className="border-b" style={{ borderColor: 'rgb(var(--line))' }}>
        <div className="mx-auto flex h-16 w-full max-w-4xl items-center px-4 sm:px-6">
          <Link to="/" aria-label="DistrxPay home"><Logo size={26} /></Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col items-center justify-center px-4 py-16 text-center sm:px-6">
        <span
          className="rounded-full border px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-faint"
          style={{ borderColor: 'rgb(var(--line))' }}
        >
          Error 404
        </span>

        <h1 className="mt-6 text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">
          We couldn’t find that page
        </h1>

        <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
          The link may be out of date, or the page may have moved.
        </p>

        {/* The address itself, because "that page" is not a useful answer when
            somebody is trying to work out which link is broken. */}
        <p className="mt-4 max-w-full truncate rounded-lg border px-3 py-2 font-mono text-[12px] text-muted"
          style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}
        >
          {pathname}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {/*
            * history.back() rather than navigate(-1): if this page was opened
            * directly — a pasted link, a bookmark — there is no history entry to
            * go back to, and the browser simply does nothing rather than
            * throwing. The home button beside it is always a way out.
            */}
          <button type="button" onClick={() => window.history.back()} className="btn btn-ghost">
            <ArrowLeft className="h-4 w-4" /> Go back
          </button>

          {restoring ? null : isAuthenticated ? (
            <Link to={home} className="btn btn-primary">
              <LayoutDashboard className="h-4 w-4" /> Your dashboard
            </Link>
          ) : (
            <Link to="/" className="btn btn-primary">
              <Home className="h-4 w-4" /> Back to the home page
            </Link>
          )}
        </div>

        <a
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Broken link: ${pathname}`)}`}
          className="mt-8 inline-flex items-center gap-2 text-[13px] text-muted transition-colors hover:text-ink"
        >
          <LifeBuoy className="h-3.5 w-3.5" aria-hidden="true" />
          Tell us this link is broken
        </a>
      </main>

      <footer className="border-t" style={{ borderColor: 'rgb(var(--line))' }}>
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-2 px-4 py-6 text-[12px] text-faint sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>© {new Date().getFullYear()} distrx</span>
          <nav className="flex items-center gap-5">
            <Link to="/terms" className="transition-colors hover:text-ink">Terms</Link>
            <Link to="/privacy" className="transition-colors hover:text-ink">Privacy</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-ink">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export default NotFoundPage;
